import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCan } from '@/hooks/useCan'
import { useMe } from '@/features/auth/hooks/useAuth'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatDateTime } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'
import { knowledgeApi, knowledgeKeys, refusal, type KnowledgeEntry, type KnowledgeStatus } from '../api'
import { KnowledgeEntryDialog } from '../components/KnowledgeEntryDialog'
import { SearchPicker } from '../components/ScopePicker'
import { ReportsTab } from '../components/ReportsTab'

/**
 * Settings -> AI -> Knowledge Base: the customer's own knowledge, which the AI agents use in their
 * answers and runbook generation uses for the objects in a runbook (Andrew, 15 Oct 2026). The
 * agents' prompts are VaultSentinel's and are not edited here. A new installation starts empty.
 */

const STATUSES = ['current', 'active', 'paused', 'retired', 'rejected', 'all'] as const
type StatusFilter = typeof STATUSES[number]

const STATUS_TINT: Record<KnowledgeStatus, string> = {
  pending: 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
  rejected: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300',
  active: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  paused: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  retired: 'bg-muted text-muted-foreground',
}

/** Why an entry needs someone to look at it, if it does. */
function reviewReasons(e: KnowledgeEntry, t: (k: string, d: string, o?: Record<string, unknown>) => string): string[] {
  const out: string[] = []
  if (e.Status === 'paused') out.push(t('knowledgeBase.flagPaused', 'Paused: an answer that used it was reported as not right'))
  else if (e.OpenReports > 0) out.push(t('knowledgeBase.flagReported', 'An answer that used it was reported as not right'))
  if (e.ObjectGone) out.push(t('knowledgeBase.flagObjectGone', '{{name}} is no longer in the estate', { name: e.ObjectName ?? '' }))
  if (e.TechnologyGone) out.push(t('knowledgeBase.flagTechnologyGone', 'Its technology is no longer in the Technologies list'))
  if (e.Status === 'active' && e.Unused30Days) out.push(t('knowledgeBase.flagUnused', 'Not used in 30 days'))
  return out
}

function RejectDialog({ entry, onClose }: { entry: KnowledgeEntry; onClose: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const reject = useMutation({
    mutationFn: () => knowledgeApi.reject(entry.EntryID, note.trim()),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: knowledgeKeys.all }); onClose() },
    onError: (e) => setError(refusal(e) ?? t('knowledgeBase.saveFailed', 'It could not be saved. Try again.')),
  })
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('knowledgeBase.rejectTitle', 'Reject this knowledge')}</DialogTitle>
          <DialogDescription>"{entry.Text}"</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="kb-reject">{t('knowledgeBase.rejectWhy', 'Why is it rejected?')}</Label>
          <Textarea id="kb-reject" rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={() => reject.mutate()} disabled={!note.trim() || reject.isPending}>{t('knowledgeBase.reject', 'Reject')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The knowledge, or (pendingOnly) what thumbs ups have saved that is waiting for approval. Andrew,
 * 15 Oct 2026: "a user has to approve the knowledge base update from thumbs up" - someone holding
 * knowledge.approve, never the person who saved it (the server holds to both).
 */
function EntriesTab({ pendingOnly = false }: { pendingOnly?: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const canManage = useCan('knowledge.manage')
  const canApprove = useCan('knowledge.approve')
  const { data: me } = useMe()
  const [rejecting, setRejecting] = useState<KnowledgeEntry | null>(null)
  const [agent, setAgent] = useState('all')
  const [objectId, setObjectId] = useState<number | null>(null)
  const [status, setStatus] = useState<StatusFilter>('current')
  const [needsReview, setNeedsReview] = useState(false)
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [dialog, setDialog] = useState<{ entry: KnowledgeEntry | null } | null>(null)

  const filters = useMemo(() => ({
    agent: agent === 'all' ? undefined : agent,
    objectId: objectId ?? undefined,
    status: pendingOnly ? 'pending' : status === 'current' ? undefined : status,
    needsReview,
    q,
  }), [agent, objectId, status, needsReview, q, pendingOnly])

  const options = useQuery({ queryKey: knowledgeKeys.options, queryFn: knowledgeApi.options })
  const list = useQuery({ queryKey: knowledgeKeys.entries(filters), queryFn: () => knowledgeApi.entries(filters) })
  const setEntryStatus = useMutation({
    mutationFn: ({ id, to }: { id: number; to: 'active' | 'paused' | 'retired' }) => knowledgeApi.update(id, { status: to }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: knowledgeKeys.all }),
  })
  const approve = useMutation({
    mutationFn: (id: number) => knowledgeApi.approve(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: knowledgeKeys.all }),
  })
  const actions = canManage || (pendingOnly && canApprove)

  const objectName = options.data?.objects.find((o) => o.id === objectId)?.name
  const objectItems = useMemo(() => (options.data?.objects ?? []).map((o) => ({ id: o.id, label: o.name, hint: o.technologyName ?? undefined })), [options.data])
  const entries = list.data ?? []

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={agent} onValueChange={setAgent}>
          <SelectTrigger className="h-8 w-48 text-xs" aria-label={t('knowledgeBase.agent', 'AI agent')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('knowledgeBase.allAgents', 'All AI agents')}</SelectItem>
            {(options.data?.agents ?? []).map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex w-56 items-center gap-1">
          <SearchPicker
            items={objectItems}
            value={objectId}
            onChange={setObjectId}
            placeholder={t('knowledgeBase.anyObject', 'Any object')}
            label={t('knowledgeBase.object', 'Object')}
            testId="filter-object"
          />
          {objectId != null && (
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={t('knowledgeBase.clearObject', 'Any object')} onClick={() => setObjectId(null)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {!pendingOnly && (
          <SegmentedControl
            size="sm"
            ariaLabel={t('knowledgeBase.status', 'Status')}
            value={status}
            onValueChange={setStatus}
            options={STATUSES.map((s) => ({ value: s, label: t(`knowledgeBase.status_${s}`, { current: 'In use, paused or waiting', active: 'In use', paused: 'Paused', retired: 'Retired', rejected: 'Rejected', all: 'All' }[s]) }))}
          />
        )}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setQ(term.trim()) }}
            onBlur={() => setQ(term.trim())}
            placeholder={t('knowledgeBase.searchWording', 'Search the wording')}
            aria-label={t('knowledgeBase.searchWording', 'Search the wording')}
            className="h-8 w-52 pl-7 text-xs"
          />
        </div>
        {!pendingOnly && (
          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox checked={needsReview} onCheckedChange={(v) => setNeedsReview(v === true)} />
            {t('knowledgeBase.needsReviewOnly', 'Needs review only')}
          </label>
        )}
        {!pendingOnly && canManage && options.data && (
          <Button size="sm" className="ml-auto h-8" onClick={() => setDialog({ entry: null })}>
            <Plus className="mr-1 h-3.5 w-3.5" />{t('knowledgeBase.add', 'Add knowledge')}
          </Button>
        )}
      </div>

      {objectName && (
        <p className="text-xs text-muted-foreground">
          {t('knowledgeBase.objectFilterNote', 'Everything an AI agent would use for {{name}}: knowledge about {{name}} itself, and knowledge for its technology.', { name: objectName })}
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[960px] text-sm">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th className="w-[34%] px-3 py-2 font-medium">{t('knowledgeBase.colKnowledge', 'Knowledge')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.agent', 'AI agent')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.appliesTo', 'Applies to')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.colConfirmed', 'Confirmed by')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.colUsed', 'Used')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.status', 'Status')}</th>
              {actions && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">{t('common.loading', 'Loading…')}</td></tr>
            )}
            {!list.isLoading && entries.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">
                  {pendingOnly
                    ? t('knowledgeBase.noneWaiting', 'Nothing is waiting for approval.')
                    : q || objectId != null || agent !== 'all' || needsReview || status !== 'current'
                      ? t('knowledgeBase.noneMatch', 'Nothing matches.')
                      : t('knowledgeBase.empty', 'No knowledge yet. Add it here, or give an AI Assistant answer a thumbs up to save the facts in it about this estate.')}
                </td>
              </tr>
            )}
            {entries.map((e) => {
              const reasons = reviewReasons(e, t)
              return (
                <tr key={e.EntryID} className={cn('border-t align-top', reasons.length > 0 && e.Status !== 'retired' && 'bg-amber-50/50 dark:bg-amber-950/20')}>
                  <td className="px-3 py-2.5">
                    <div>{e.Text}</div>
                    {e.Status === 'rejected' && e.DecisionNote && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {t('knowledgeBase.rejectedBy', 'Rejected by {{name}}: {{note}}', { name: e.ApprovedByName ?? '', note: e.DecisionNote })}
                      </div>
                    )}
                    {e.Status !== 'retired' && reasons.map((r) => (
                      <div key={r} className="mt-1 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                        <AlertTriangle className="h-3 w-3" />{r}
                      </div>
                    ))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <div>{e.AgentName}</div>
                    <div className="text-xs text-muted-foreground">
                      {e.Scope === 'object'
                        ? t('knowledgeBase.usedByAny', 'used by any agent')
                        : t('knowledgeBase.usedByOwn', 'used by the {{agent}} agent', { agent: e.AgentName })}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('rounded-full px-2 py-0.5 text-[11px]', e.Scope === 'object' ? 'bg-muted' : 'bg-indigo-50 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300')}>
                      {e.Scope === 'object' ? t('knowledgeBase.scopeObject', 'One object') : t('knowledgeBase.scopeTechnology', 'A technology')}
                    </span>
                    <div className="mt-1">
                      {e.Scope === 'object'
                        ? e.ObjectName
                        : t('knowledgeBase.allOfTechnology', 'All {{technology}} in this estate', { technology: e.TechnologyName ?? '' })}
                    </div>
                    {e.Scope === 'technology' && (
                      <div className="text-xs text-muted-foreground">{t('knowledgeBase.reaches', 'reaches {{n}} objects today', { n: e.TechnologyObjects ?? 0 })}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div>{e.ConfirmedByName ?? '—'}</div>
                    {e.Status !== 'rejected' && e.ApprovedByName && (
                      <div className="text-xs text-muted-foreground">{t('knowledgeBase.approvedBy', 'approved by {{name}}', { name: e.ApprovedByName })}</div>
                    )}
                    <div className="text-xs text-muted-foreground">{formatDateTime(e.ConfirmedAt)}</div>
                    <div className="text-xs text-muted-foreground" title={e.SourceQuestion ?? undefined}>
                      {e.Source === 'answer' ? t('knowledgeBase.sourceAnswer', 'From an AI Assistant answer')
                        : e.Source === 'report' ? t('knowledgeBase.sourceReport', 'Correcting a report')
                          : t('knowledgeBase.sourceManual', 'Added here')}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <div>{e.Uses ? t('knowledgeBase.usedTimes', '{{n}} times', { n: e.Uses }) : t('knowledgeBase.neverUsed', 'Never')}</div>
                    {e.LastUsedAt && <div className="text-xs text-muted-foreground">{t('knowledgeBase.lastUsed', 'last {{when}}', { when: formatDateTime(e.LastUsedAt) })}</div>}
                    {e.RunbookUses > 0 && <div className="text-xs text-muted-foreground">{t('knowledgeBase.inRunbooks', 'in {{n}} runbook generations', { n: e.RunbookUses })}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[11px]', STATUS_TINT[e.Status])}>
                      {t(`knowledgeBase.status_${e.Status}`, { pending: 'Waiting for approval', active: 'In use', paused: 'Paused', retired: 'Retired', rejected: 'Rejected' }[e.Status])}
                    </span>
                  </td>
                  {actions && e.Status === 'pending' && (
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      {canApprove && e.ConfirmedBy !== me?.id ? (
                        <>
                          <Button size="sm" className="h-7 px-2 text-xs" disabled={approve.isPending} onClick={() => approve.mutate(e.EntryID)}>
                            <Check className="mr-1 h-3.5 w-3.5" />{t('knowledgeBase.approve', 'Approve')}
                          </Button>{' '}
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-destructive" onClick={() => setRejecting(e)}>
                            {t('knowledgeBase.reject', 'Reject')}
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t('knowledgeBase.awaitingOther', 'Waiting for someone else to approve')}</span>
                      )}
                    </td>
                  )}
                  {actions && e.Status === 'rejected' && <td />}
                  {actions && e.Status !== 'pending' && e.Status !== 'rejected' && (
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      {canManage && options.data && e.Status !== 'retired' && (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setDialog({ entry: e })}>
                          {t('knowledgeBase.edit', 'Edit')}
                        </Button>
                      )}{' '}
                      {canManage && e.Status !== 'active' && (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                                onClick={() => setEntryStatus.mutate({ id: e.EntryID, to: 'active' })}>
                          {t('knowledgeBase.putBack', 'Put back in use')}
                        </Button>
                      )}{' '}
                      {canManage && e.Status !== 'retired' && (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-destructive"
                                onClick={() => setEntryStatus.mutate({ id: e.EntryID, to: 'retired' })}>
                          {t('knowledgeBase.retire', 'Retire')}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('knowledgeBase.footer', 'Knowledge about one object is used by any AI agent working on it. Technology knowledge is used by its own agent, for every object of that technology in this estate. Retired knowledge is kept in the history but never used.')}
      </p>

      {dialog && options.data && (
        <KnowledgeEntryDialog open onOpenChange={(o) => { if (!o) setDialog(null) }} options={options.data} entry={dialog.entry} />
      )}
      {rejecting && <RejectDialog entry={rejecting} onClose={() => setRejecting(null)} />}
    </div>
  )
}

export function KnowledgeBasePage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'reports' ? 'reports' : params.get('tab') === 'approvals' ? 'approvals' : 'knowledge'
  const open = useQuery({ queryKey: knowledgeKeys.reports('open'), queryFn: () => knowledgeApi.reports('open') })
  const waiting = useQuery({ queryKey: knowledgeKeys.entries({ status: 'pending' }), queryFn: () => knowledgeApi.entries({ status: 'pending' }) })

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('knowledgeBase.title', 'Knowledge Base')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('knowledgeBase.subtitle', "What your people have confirmed about this estate. The AI agents use it in their answers, and runbook generation uses it for the objects in a runbook. The agents' own instructions come from VaultSentinel and are not edited here.")}
        </p>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams(v === 'knowledge' ? {} : { tab: v }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="knowledge">{t('knowledgeBase.tabKnowledge', 'Knowledge')}</TabsTrigger>
          <TabsTrigger value="approvals">
            {t('knowledgeBase.tabApprovals', 'Waiting for approval')}
            {(waiting.data?.length ?? 0) > 0 && (
              <span className="ml-1.5 rounded-full bg-sky-100 px-1.5 text-[11px] text-sky-900 dark:bg-sky-900/40 dark:text-sky-200">
                {waiting.data?.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="reports">
            {t('knowledgeBase.tabReports', 'Not right reports')}
            {(open.data?.length ?? 0) > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[11px] text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                {t('knowledgeBase.openCount', '{{n}} open', { n: open.data?.length ?? 0 })}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="knowledge"><EntriesTab /></TabsContent>
        <TabsContent value="approvals"><EntriesTab pendingOnly /></TabsContent>
        <TabsContent value="reports"><ReportsTab /></TabsContent>
      </Tabs>
    </div>
  )
}

export default KnowledgeBasePage
